import { useEffect, useId, useRef, useState } from 'react';
import { Check, Crown, Droplet, HeartPulse, MapPin, Search } from 'lucide-react';
import Screen from '../../demo/Screen';
import './BookingDoubts.css';

const PACKAGES = [
  {
    key: 'basic',
    line1: 'Basic',
    line2: 'Health Check',
    Icon: Droplet,
    tone: 'red',
  },
  {
    key: 'comprehensive',
    line1: 'Comprehensive',
    line2: 'Package',
    Icon: Crown,
    tone: 'amber',
  },
  {
    key: 'executive',
    line1: 'Executive',
    line2: 'Health Check',
    Icon: HeartPulse,
    tone: 'blue',
  },
];

const LABS = [
  { key: 'lab-a', km: '1.2 km', rating: 4.5 },
  { key: 'lab-b', km: '2.1 km', rating: 4 },
  { key: 'lab-c', km: '2.8 km', rating: 4.5 },
];

const ROWS = ['r1', 'r2', 'r3', 'r4'];
const STARS = [1, 2, 3, 4, 5];

const NEXT_KEYS = ['ArrowRight', 'ArrowDown'];
const PREV_KEYS = ['ArrowLeft', 'ArrowUp'];

/*
 * On arrival the three lab rows, then the three package cards, glow one after
 * another (FLASH_STEP_MS apart), once per visit. Then the visuals rest.
 */
const FLASH_DELAY_MS = 400;
const FLASH_STEP_MS = 350;
const FLASH_ITEMS = LABS.length + PACKAGES.length;
export const FLASH_TOTAL_MS = FLASH_DELAY_MS + FLASH_ITEMS * FLASH_STEP_MS + 200;

const prefersStill = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- The illustration from the reference art (its thought bubbles painted out, 2026-10-24) ---------- */

const ART_SRC = '/demo/problem/woman-doubts.jpg';
const ART_ALT = 'A young woman at her desk with a laptop and her phone, looking unsure.';

function Thinker() {
  return (
    <figure className="bdoubt-art">
      <img className="bdoubt-art__img" src={ART_SRC} alt={ART_ALT} width="598" height="1006" decoding="async" />
    </figure>
  );
}

/* ---------- Card 1: lab search ---------- */

function Stars({ rating }) {
  const uid = useId();
  const half = `${uid}-half`;
  return (
    <svg className="bdoubt-labs__stars" viewBox="0 0 92 16" focusable="false">
      <defs>
        <linearGradient id={half} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0.5" stopColor="#f59e0b" />
          <stop offset="0.5" stopColor="#dfe4ec" />
        </linearGradient>
      </defs>
      {STARS.map((n) => {
        let fill = '#dfe4ec';
        if (rating >= n) fill = '#f59e0b';
        else if (rating >= n - 0.5) fill = `url(#${half})`;
        return (
          <path
            key={n}
            transform={`translate(${(n - 1) * 19} 0)`}
            d="M8 .8l2.2 4.6 5 .6-3.7 3.5.9 5L8 12.1l-4.4 2.4.9-5L.8 6l5-.6Z"
            fill={fill}
          />
        );
      })}
    </svg>
  );
}

function LabSearch() {
  return (
    <div className="bdoubt-labs" aria-hidden="true">
      <span className="bdoubt-labs__search">
        <Search size={18} strokeWidth={2.6} />
        Diagnostic labs near me
      </span>
      {LABS.map((lab, i) => (
        <span className="bdoubt-labs__row bdoubt-flash" key={lab.key} style={{ '--flash-i': i }}>
          <img className="bdoubt-labs__thumb" src={`/demo/problem/lab-${i + 1}.jpg`} alt="" width="97" height="71" />
          <span className="bdoubt-labs__meta">
            <span className="bdoubt-labs__bar" />
            <Stars rating={lab.rating} />
          </span>
          <span className="bdoubt-labs__km">
            <MapPin size={16} strokeWidth={2.4} />
            {lab.km}
          </span>
        </span>
      ))}
    </div>
  );
}

/* ---------- Card 2: package picker ---------- */

function PackagePicker() {
  const [picked, setPicked] = useState('basic');
  const inputs = useRef({});
  const pickedPkg = PACKAGES.find((p) => p.key === picked);

  function onKeyDown(event, index) {
    const step = NEXT_KEYS.includes(event.key) ? 1 : PREV_KEYS.includes(event.key) ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = PACKAGES[(index + step + PACKAGES.length) % PACKAGES.length];
    setPicked(next.key);
    inputs.current[next.key]?.focus();
  }

  return (
    <div className="bdoubt-pick">
      <div className="bdoubt-pkgs" role="radiogroup" aria-label="Pick a package">
        {PACKAGES.map((pkg, index) => {
          const checked = picked === pkg.key;
          const { Icon } = pkg;
          return (
            <label
              key={pkg.key}
              className={`bdoubt-pkg bdoubt-flash bdoubt-flash--control${checked ? ' is-checked' : ''}`}
              style={{ '--flash-i': LABS.length + index }}
            >
              <input
                ref={(el) => {
                  inputs.current[pkg.key] = el;
                }}
                className="bdoubt-pkg__input"
                type="radio"
                name="booking-doubts-package"
                value={pkg.key}
                checked={checked}
                onChange={() => setPicked(pkg.key)}
                onKeyDown={(e) => onKeyDown(e, index)}
                data-trapezium-id={`problem.booking-doubts.pick-${pkg.key}`}
              />
              <span className={`bdoubt-pkg__icon bdoubt-pkg__icon--${pkg.tone}`} aria-hidden="true">
                <Icon size={22} strokeWidth={2.2} />
              </span>
              <span className="bdoubt-pkg__name">
                {pkg.line1} <span className="bdoubt-pkg__br">{pkg.line2}</span>
              </span>
              <span className="bdoubt-pkg__rows" aria-hidden="true">
                {ROWS.map((r) => (
                  <span key={r}>
                    <Check size={14} strokeWidth={3.2} />
                  </span>
                ))}
              </span>
              <span className="bdoubt-pkg__radio" aria-hidden="true">
                {checked && <Check size={14} strokeWidth={3.4} />}
              </span>
            </label>
          );
        })}
      </div>
      <p className="bdoubt-picked" role="status" data-trapezium-state="package-selected">
        Selected:{' '}
        <strong>
          {pickedPkg.line1} {pickedPkg.line2}
        </strong>
      </p>
    </div>
  );
}

export default function BookingDoubts() {
  const [flash, setFlash] = useState(() => (prefersStill() ? 'rested' : 'flashing'));

  /* One pass per visit: the timer runs once, then the visuals rest for good. */
  useEffect(() => {
    if (flash !== 'flashing') return undefined;
    const timer = setTimeout(() => setFlash('rested'), FLASH_TOTAL_MS);
    return () => clearTimeout(timer);
  }, [flash]);

  return (
    <Screen id="problem.booking-doubts" title="Why they pick someone else" className="bdoubt">
      <p className="eyebrow bdoubt-eyebrow">Problem · 2 of 2</p>
      <h1 className="headline bdoubt-headline">
        <em>Ready to book.</em> But unsure about two things.
      </h1>

      <div className="bdoubt-layout">
        <Thinker />

        <div
          className={`bdoubt-cards${flash === 'flashing' ? ' is-flashing' : ''}`}
          data-trapezium-state={flash}
        >
          <section className="card bdoubt-card" aria-labelledby="bdoubt-where">
            <span className="bdoubt-card__num" aria-hidden="true">
              1
            </span>
            <div className="bdoubt-card__text">
              <p className="bdoubt-card__eyebrow">Where to go</p>
              <h2 id="bdoubt-where">Where should I get this done?</h2>
              <p className="bdoubt-card__sub">All labs look the same.</p>
              <p className="bdoubt-card__body">
                Every option makes the same promises, and it’s hard to know which lab to trust.
              </p>
            </div>
            <LabSearch />
          </section>

          <section className="card bdoubt-card" aria-labelledby="bdoubt-what">
            <span className="bdoubt-card__num" aria-hidden="true">
              2
            </span>
            <div className="bdoubt-card__text">
              <p className="bdoubt-card__eyebrow">What to book</p>
              <h2 id="bdoubt-what">Which package do I need?</h2>
              <p className="bdoubt-card__sub">Nobody is helping me pick.</p>
              <p className="bdoubt-card__body">
                Your website lists packages, but it doesn’t explain what they include or which one is right
                for me.
              </p>
            </div>
            <PackagePicker />
          </section>
        </div>
      </div>
    </Screen>
  );
}
